import api from './api';

/**
 * Requests Service
 * Handles all request-related API calls for users
 */
const requestsService = {
    exportMyRequests: async (format = 'xlsx') => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        const token = localStorage.getItem('msk_user_access_token') || localStorage.getItem('msk_aqar_access_token');

        const response = await fetch(`${API_BASE_URL}/user/requests/export?format=${encodeURIComponent(format)}`, {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
        });

        if (!response.ok) {
            throw new Error('Failed to export requests');
        }

        return response.blob();
    },

    importFromFile: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.postMultipart('/user/requests/import', formData);
    },

    /**
     * Get all requests
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getAll: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/requests${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get my requests
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    getMyRequests: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/user/requests${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get request by ID
     * @param {string} id - Request ID
     * @returns {Promise<Object>}
     */
    getById: async (id) => {
        return api.get(`/user/requests/${id}`);
    },

    /**
     * Create new request
     * @param {Object} data - Request data
     * @returns {Promise<Object>}
     */
    create: async (data) => {
        // Transform data to match backend expected format
        const transformedData = {
            clientName: data.clientName,
            clientPhone: data.clientPhone,
            propertyType: data.propertyType,
            requestType: data.requestType || 'buy',
            city: data.city,
            district: data.district || (data.districts && data.districts[0]) || '',
            districts: data.districts || [],
            budget: data.budget,
            areaMin: data.areaMin || data.area || '',
            areaMax: data.areaMax || '',
            paymentMethod: data.paymentMethod,
            paymentPlan: data.paymentPlan,
            age: data.age,
            floor: data.floor,
            clientSeriousness: data.clientSeriousness,
            lastContact: data.lastContact,
            requirements: data.requirements,
            notes: data.notes,
            matchingEnabled: data.matchingEnabled,
            shareInNetwork: data.shareInNetwork,
            confirmDuplicate: data.confirmDuplicate || false
        };
        return api.post('/user/requests', transformedData);
    },

    /**
     * Update request
     * @param {string} id - Request ID
     * @param {Object} data - Updated request data
     * @returns {Promise<Object>}
     */
    update: async (id, data) => {
        // Transform data to match backend expected format
        const transformedData = {
            clientName: data.clientName,
            clientPhone: data.clientPhone,
            propertyType: data.propertyType,
            requestType: data.requestType || 'buy',
            city: data.city,
            district: data.district || (data.districts && data.districts[0]) || '',
            districts: data.districts || [],
            budget: data.budget,
            areaMin: data.areaMin || data.area || '',
            areaMax: data.areaMax || '',
            paymentMethod: data.paymentMethod,
            paymentPlan: data.paymentPlan,
            age: data.age,
            floor: data.floor,
            clientSeriousness: data.clientSeriousness,
            lastContact: data.lastContact,
            requirements: data.requirements,
            notes: data.notes,
            matchingEnabled: data.matchingEnabled,
            shareInNetwork: data.shareInNetwork,
            status: data.status
        };
        return api.put(`/user/requests/${id}`, transformedData);
    },

    /**
     * Delete request
     * @param {string} id - Request ID
     * @returns {Promise<Object>}
     */
    delete: async (id) => {
        return api.delete(`/user/requests/${id}`);
    },

    /**
     * Archive request (soft delete)
     * @param {string} id - Request ID
     * @returns {Promise<Object>}
     */
    archive: async (id) => {
        return api.patch(`/user/requests/${id}/archive`);
    },

    /**
     * Change request status
     * @param {string} id - Request ID
     * @param {string} status - New status
     * @returns {Promise<Object>}
     */
    changeStatus: async (id, status) => {
        return api.patch(`/user/requests/${id}/status`, { status });
    },

    /**
     * Approve or reject a request received from the public marketer site
     * @param {string} id - Request ID
     * @param {'approve'|'reject'} action - Review action
     * @returns {Promise<Object>}
     */
    reviewPublicMarketerRequest: async (id, action) => {
        return api.patch(`/user/requests/${id}/review-public`, { action });
    },

    /**
     * Toggle matching for request
     * @param {string} id - Request ID
     * @returns {Promise<Object>}
     */
    toggleMatching: async (id) => {
        return api.patch(`/user/requests/${id}/matching`);
    },

    /**
     * Toggle network visibility for request
     * @param {string} id - Request ID
     * @returns {Promise<Object>}
     */
    toggleNetwork: async (id) => {
        return api.patch(`/user/requests/${id}/network`);
    },

    /**
     * Update visible fields for network
     * @param {string} id - Request ID
     * @param {Array<string>} visibleFields - Fields to show in network
     * @returns {Promise<Object>}
     */
    updateNetworkVisibility: async (id, visibleFields) => {
        return api.patch(`/user/requests/${id}/network`, { visibleFields });
    },

    /**
     * Submit public request (no auth required)
     * @param {Object} data - Request data from public form
     * @returns {Promise<Object>}
     */
    submitPublicRequest: async (data) => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        
        const transformedData = {
            clientName: data.clientName,
            clientPhone: data.clientPhone,
            propertyType: data.propertyType || 'villa',
            requestType: data.requestType || 'buy',
            city: data.city,
            district: data.district || (data.districts && data.districts[0]) || '',
            districts: data.districts || [],
            budget: data.budget || 0,
            paymentMethod: data.paymentMethod,
            paymentPlan: data.paymentPlan,
            age: data.age,
            floor: data.floor,
            requirements: data.requirements,
            preferredContact: data.preferredContact,
            userId: data.referralUserId || null
        };

        const response = await fetch(`${API_BASE_URL}/public/requests`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(transformedData)
        });

        const result = await response.json();
        
        if (!response.ok) {
            throw new Error(result.message || 'حدث خطأ في إرسال الطلب');
        }
        
        return result;
    },

    /**
     * Get matching offers for a public request (no auth)
     * @param {string} requestId - The request _id returned after submission
     * @returns {Promise<Object>}
     */
    getPublicRequestMatches: async (requestId) => {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';
        const response = await fetch(`${API_BASE_URL}/public/requests/${requestId}/matches`);
        const result = await response.json();
        return result;
    },

    /**
     * Analyze request text with AI
     * @param {string} text - Request description text
     * @param {string} cityHint - Optional city hint for better matching
     * @returns {Promise<Object>}
     */
    analyzeWithAI: async (text, cityHint = '') => {
        return api.post('/user/requests/analyze-ai', { text, city: cityHint });
    }
};

export default requestsService;
